import { StringDate } from '../models/common';
import { Account, PartialAccount } from '../account/model';
import { Advisor } from '../advisor/model';
import { Firm } from '../firm/model';
import { HoldingOption } from '../holding-option/model';
import { OrderApproval, OrderApprovalConfig, OrderCurrentApproval } from '../order-approve/model';
import { OrderForm } from '../order-form/model';
import { Product } from '../product/model';
import { ProfileRepCode } from '../profile/model';

export type PurchaseType = 'Initial' | 'Additional';

export interface OrderId {
    id: string;
    validation?: OrderValidation;
}

export interface OrderStatusFilter {
    filter: string;
    label: string;
    description: string;
    relatedFilters?: string[];
    display: boolean;
    group: number;
    count: number;
}

export interface PartialOrder {
    id?: string;
    registrationId?: string;
    account?: PartialAccount;
    customData?: Record<string, unknown>;
    orderSpecificData?: Record<string, unknown>;
    eSign?: ElectronicSignature;
    repCodeId: string;
    fundId: number;
    holdingOptionId: string;
    purchaseType: PurchaseType;
    purchaseAmount: number;
    advisorId?: string;
    externalFirmId?: string;
}

export interface OrderForms {
    [formId: string]: OrderForm;
}

export interface OrderValidationFailure {
    code: string;
    debugInfo: string;
    message: string;
    path: string;
}

export interface OrderValidation {
    failures: OrderValidationFailure[];
    orderPassed: boolean;
}

export type eSignEnvelopeRecipientType = 'investor' | 'advisor' | 'additional' | 'postAdvisor';
export type eSignEnvelopeRecipientStatusType =
    | 'AutoResponded'
    | 'Completed'
    | 'Created'
    | 'Declined'
    | 'Delivered'
    | 'Sent';
// 'Voided' is written verbatim by the API from DocuSign, including when our own cancelEnvelope voids
// one. Leaving it out failed the whole order decode, so a voided envelope made the order unloadable.
export type eSignEnvelopeStatusType =
    | 'Completed'
    | 'Created'
    | 'Declined'
    | 'Delivered'
    | 'Sent'
    | 'Voided';
export type eSignEnvelopeIdCheckType = 'sms' | 'kba' | 'phone';

export interface eSignEnvelopeRecipient {
    id?: string;
    type: eSignEnvelopeRecipientType;
    fullName: string;
    email: string;
    phoneMobile?: string;
    status?: eSignEnvelopeRecipientStatusType;
    timestamp?: string;
    routingOrder: number;
    /**
     * The signature role this recipient signs under, and the key every recipient-level admin action
     * is addressed by (trade-platform#10428). For a canonical signer it is the signature role
     * itself ('Investor', 'Advisor'); for a Custom or MarketplacePrincipal signer it is the bare
     * roleName the form supplied, which is expected to collide with a canonical role.
     *
     * Optional because an envelope persisted before the field existed would otherwise fail the
     * whole order decode and make the order unloadable — the same trap 'Voided' fell into above. A
     * recipient without one cannot be addressed, so the admin panel leaves it out of its list.
     */
    role?: string;
    /**
     * Roles folded into this recipient because they resolved to the same person. A Custom signer
     * sharing the advisor's address gets no recipient of its own and lands here instead, which is
     * why the admin panel offers these as removable in their own right.
     */
    mergedRoles?: string[];
    /**
     * False for the receive-only rows a mappable order carries — the advisor is added for the
     * mapping UI whether or not they sign. They are recipients but not signers, so they are not
     * offered for removal.
     */
    hasSignatureFields?: boolean;
}

export interface eSignEnvelope {
    id?: string;
    status?: eSignEnvelopeStatusType;
    created?: string;
    sent?: string;
    idCheckConfig?: eSignEnvelopeIdCheckType;
    recipients?: eSignEnvelopeRecipient[];
}

export interface MappedSignatures {
    status?: string;
}

export interface ElectronicSignature {
    isAvailable?: boolean;
    acceptedByUser?: boolean;
    isSelected?: boolean;
    reasons?: string[];
    envelope?: eSignEnvelope;
    wetSignRoles?: string[];
    mappedSignatures?: MappedSignatures;
}

export interface BaseOrder {
    title: string;
    id: string;
    status: string;
    statusLabel: string;
    completed: number;
    orderNumber: number;
    customData: Record<string, unknown>;
    orderSpecificData: Record<string, unknown>;
    account?: Account | null;
    advisor?: Advisor;
    holdingOption?: HoldingOption;
    fund: Product;
    forms: OrderForms;
    files: DocumentFile[];
    requiredSupplementalFiles: RequiredDocument[];
    repCode: ProfileRepCode;
    signingStatus: any;
    validation?: OrderValidation;
    approvals?: OrderApproval[];
    currentApproval?: OrderCurrentApproval;
    firm: Firm;
    envelopeId: string;
    eSign?: ElectronicSignature;
    wetSign?: boolean;
    tooltip?: string;
    createdBy?: Advisor;
    presignatureReview?: PresignatureReview;
    riskCategory?: string;
    settlementDetails: SettlementDetails | null;
}

export interface ReviewStepConfig {
    additionalData?: any;
    createdAt: string;
    custodianId?: string;
    delegateEntityId?: string;
    delegateEntityType?: string;
    deletedAt?: string;
    firmId: number;
    fundSponsorId?: string;
    id: number;
    name: string;
    role: string;
    rules?: string;
    sequence: number;
    subType?: string;
    type: string;
    updatedAt: string;
}

export type ReviewStepStatus = 'not yet sent' | 'pending' | 'approved';
export interface ReviewStep {
    approvalName: string;
    entityType: string;
    name: string;
    status: ReviewStepStatus;
    updatedAt?: string;
}

export interface PresignatureReview {
    required: boolean;
    steps: ReviewStep[];
}

export interface Order extends BaseOrder {
    account: Account;
    purchaseAmount: number;
    purchaseType: string;
}

export interface SettlementDetails {
    markComplete?: boolean;
    tradeDate?: StringDate;
    purchaseAmount?: number;
    settlementDate?: StringDate;
    pricePerShare?: number;
    numberOfShares?: number;
    fundAccountNumber?: string;
    processingNotes?: string;
    markedCompletedDate?: StringDate; // not sent by UI, returned by API
    markedCompletedByFirm?: string; // not sent by UI, returned by API
    updatedByFirm?: string;
    updatedAt?: StringDate;
}

export interface SupplementalFile {
    id: string;
    name: string;
    tooltip?: string;
}

export interface RequiredDocument {
    id: string;
    contextMap?: string;
    dismissedDate?: string;
    dismissedReason?: string;
    required?: boolean;
    documents?: string[];
    type?: string;
    tooltip?: string;
    firmId?: number;
    fundId?: number;
    holdingOptionId?: number;

    descriptionHeader?: string;
    descriptionFooter?: string;
    descriptionBullets?: string[];

    // For internal usage, this doesn't come from the api
    entityName?: string | null;
}

export interface DocumentFile {
    id: string;
    name: string;
    sizeInBytes?: number;
    formId?: string;
    type: string;
    supplementalFileTypeId?: string;
    supplementalFileType?: SupplementalFileType;
    contextMap?: string;
    isPrivate?: boolean;
    isUserUploaded?: boolean;
    originalUploadedAt?: string;
    createdAt?: string;
    orderStatus?: string;
    config?: OrderApprovalConfig;
    firmId?: number;
    fundId?: number;
    holdingOptionId?: number;
    // Write-only: sent when labelling a form with the advisor's own firm. The api never returns it
    // on a file — it is stored on the order's form, not the file row.
    externalFirmId?: number | null;

    // For internal usage, these don't come from the api
    isRequired?: boolean;
    screenName?: string;
    index?: number;
    descriptionHeader?: string;
    descriptionFooter?: string;
    descriptionBullets?: string[];
}

export interface SupplementalFileType {
    name: string;
}

export type OrderRecord = Pick<
    BaseOrder & {
        fundName: string;
        ticker: string;
        updatedAt: string;
        role?: string;
        queueLabel: string;
        firmId: number;
        firmName: string;
        advisorFirstName: string;
        advisorLastName: string;
        advisorFullName?: string;
        additionalAdvisorNames?: string;
        purchaseAmount?: number;
        custodianName: string;
        createdByName: string;
        createdAt: string;
        amount?: number;
        riskCategory?: string;
        isOnboarded: Product['isOnboarded'];
    },
    | 'id'
    | 'orderNumber'
    | 'purchaseAmount'
    | 'status'
    | 'title'
    | 'fundName'
    | 'custodianName'
    | 'ticker'
    | 'queueLabel'
    | 'updatedAt'
    | 'role'
    | 'repCode'
    | 'firmId'
    | 'firmName'
    | 'advisorFirstName'
    | 'advisorLastName'
    | 'advisorFullName'
    | 'additionalAdvisorNames'
    | 'createdByName'
    | 'createdAt'
    | 'amount'
    | 'riskCategory'
    | 'isOnboarded'
>;

export interface OrderStatus {
    status: string;
    name: string;
    role: string;
    firm: string;
    updatedAt: number;
}

export interface OrderIdPayload {
    orderId: string;
    required?: boolean;
    skipReview?: boolean;
}

/**
 * Maximum length of the optional note an advisor can attach on the finalize step.
 * Must stay in sync with the `note` limit the advisors-api submit routes enforce.
 */
export const ORDER_SUBMISSION_NOTE_MAX_LENGTH = 2000;

/**
 * Submitting an order carries an optional free-text note, which the API records as an
 * information comment on the order. refs trade-platform#10392
 */
export interface OrderSubmitPayload extends OrderIdPayload {
    note?: string;
}

export type OrderType = 'buy' | 'maintenance' | 'sell';
export type LoadOrderSuffix = OrderType;

export interface LoadOrderPayload extends OrderIdPayload {
    reducerSuffix: LoadOrderSuffix;
}

export type UpdateOrderSuffix = 'orderUpdate' | 'orderProgressUpdate';

export interface UpdateOrderPayload {
    updatePayload: BaseOrder;
    updatePayloadExtras: {
        formCompleted: boolean;
    };
    reducerSuffix: UpdateOrderSuffix;
}

export type UpdateOrderFilesSuffix = 'orderFilesUpdate';

export interface UpdateOrderFilesPayload {
    updatePayload: DocumentFile[];
    updatePayloadExtras: {
        orderId: string;
    };
    reducerSuffix: UpdateOrderFilesSuffix;
}

export interface OrderReducerSuffixPayload {
    reducerSuffix: string;
}

export interface UpdateOrderSuffixPayload {
    reducerSuffix: UpdateOrderSuffix;
}

export interface OrdersStatusHistory {
    status: string;
    updatedBy: string;
    updatedByOrganization: string;
    updatedByType: string;
    createdAt: string;
    role: string;
}

export interface IfgGatewayIntegrationFile {
    id: number;
    displayName: string;
    isSuccess?: boolean;
    message?: string;
    lastSent?: string;
    type?: string;
    fileData?: IfgGatewayIntegrationFileFileData;
}

export interface IfgGatewayIntegrationFileFileData {
    url: string;
    version: string;
    bucket: string;
    MB: string;
    fileId?: string;
    userId?: string;
    ticketId?: string;
}

/**
 * One signer queue (an OSJ, in IFG's vocabulary) an order can be moved to.
 *
 * `configId` is the ApprovalConfig the move repoints the pending approval at; `label` is the
 * display name; `value` is the branch string the server writes onto the order. `value` is carried
 * here for display and debugging only — the move is requested by `configId` and the server
 * re-derives the branch from the config, so the UI can never write a branch the target config's
 * own rule does not match.
 */
export interface OrderQueueOption {
    configId: number;
    label: string;
    value: string;
    isCurrent: boolean;
}

export interface OrderQueueOptions {
    /** The config the order's pending approval currently sits on, or null if it has none. */
    currentConfigId: number | null;
    /**
     * The name of that queue. Not always one of `options`: a retired config is somewhere an order
     * can still BE but not somewhere it may be moved TO.
     */
    currentLabel: string | null;
    options: OrderQueueOption[];
}

export type OrderRecipientChangeAction = 'removed' | 'emailChanged';

/**
 * An order admin's change to one e-sign recipient (trade-platform#10428).
 *
 * The server records it as `order.eSign.recipientOverrides` and re-applies it as an input to every
 * later envelope rebuild, rather than editing `eSign.envelope.recipients` — that array is
 * regenerated from scratch whenever the order's files change, so an edit written into it is undone
 * the next time anything touches the order.
 *
 * `originalEmail` is not decoration. The server resolves the change against the recipient holding
 * that address, which is what keeps a removal off a namesake role — a Custom signer's role is the
 * bare roleName its form supplied and is expected to collide with a canonical one — and what makes
 * the override expire if the role is later re-pointed at somebody else. Always send it.
 */
export interface OrderRecipientChange {
    role: string;
    originalEmail: string;
    action: OrderRecipientChangeAction;
    /** Required when `action` is `emailChanged`, and meaningless otherwise. */
    newEmail?: string;
    reason?: string;
}
